package com.milta.feature.aicredit.presentation

import com.milta.core.mvi.MviViewModel
import com.milta.core.network.ApiException
import com.milta.feature.aicredit.domain.entity.AiCreditSettings
import com.milta.feature.aicredit.domain.usecase.GetAiCreditSettingsUseCase
import com.milta.feature.aicredit.domain.usecase.UpdateAiCreditSettingsUseCase
import kotlinx.coroutines.launch

/** The 6 fields of the "Tokens IA" tab (R10, R12). */
enum class AiCreditSettingsField { EnterpriseTrial, EnterprisePaid, DriverTrial, DriverPaid, UsdPerEur, LowThreshold }

/** Per-field validation error, resolved to a localized message by the screen. */
enum class AiCreditSettingsFieldError { Required, MustBePositive, LimitTooHigh, RateTooHigh, ThresholdOutOfRange }

/** Server caps (AiCreditSettings validation): limits ≤ 1000 €, rate ≤ 10, threshold 1 to 99. */
const val AI_CREDIT_MAX_LIMIT_EUR = 1000.0
const val AI_CREDIT_MAX_USD_PER_EUR = 10.0
const val AI_CREDIT_MIN_THRESHOLD = 1
const val AI_CREDIT_MAX_THRESHOLD = 99

data class AdminAiCreditSettingsState(
    val isLoading: Boolean = true,
    /** "server_error" | "offline" — the settings could not be read at all. */
    val loadError: String? = null,
    val values: Map<AiCreditSettingsField, String> = emptyMap(),
    val fieldErrors: Map<AiCreditSettingsField, AiCreditSettingsFieldError> = emptyMap(),
    val isSaving: Boolean = false,
    /** Non-null right after a successful save — drives the "Limites enregistrées" banner. */
    val savedSettings: AiCreditSettings? = null,
    /** "validation" (unknown server 400) | "server_error" | "offline". */
    val saveError: String? = null,
) {
    /** Rate typed in the form, for the live weighted-token equivalents; null while invalid. */
    val usdPerEur: Double? get() = values[AiCreditSettingsField.UsdPerEur]?.let(AiCreditFormat::parseDecimal)?.takeIf { it > 0 }
}

sealed interface AdminAiCreditSettingsIntent {
    data object Load : AdminAiCreditSettingsIntent
    data class UpdateField(val field: AiCreditSettingsField, val value: String) : AdminAiCreditSettingsIntent
    data object Save : AdminAiCreditSettingsIntent
}

/** MIL-98 — Admin "Limites d'usage", tab "Tokens IA" (A1). Web-only. [separator]: decimal mark shown in the fields. */
class AdminAiCreditSettingsViewModel(
    private val getSettings: GetAiCreditSettingsUseCase,
    private val updateSettings: UpdateAiCreditSettingsUseCase,
    private val separator: String = ",",
) : MviViewModel<AdminAiCreditSettingsState, AdminAiCreditSettingsIntent>(AdminAiCreditSettingsState()) {

    override fun onIntent(intent: AdminAiCreditSettingsIntent) {
        when (intent) {
            AdminAiCreditSettingsIntent.Load -> load()
            is AdminAiCreditSettingsIntent.UpdateField -> setState {
                copy(
                    values = values + (intent.field to sanitize(intent.field, intent.value)),
                    fieldErrors = fieldErrors - intent.field,
                    savedSettings = null,
                    saveError = null,
                )
            }
            AdminAiCreditSettingsIntent.Save -> save()
        }
    }

    private fun sanitize(field: AiCreditSettingsField, value: String): String = when (field) {
        AiCreditSettingsField.LowThreshold -> value.filter { it.isDigit() }.take(THRESHOLD_DIGITS)
        AiCreditSettingsField.UsdPerEur -> AiCreditFormat.sanitizeAmountInput(value, maxDecimals = RATE_DECIMALS, maxWholeDigits = RATE_WHOLE_DIGITS)
        else -> AiCreditFormat.sanitizeAmountInput(value, maxDecimals = EUR_DECIMALS)
    }

    private fun load() {
        setState { copy(isLoading = true, loadError = null) }
        scope.launch {
            getSettings().fold(
                onSuccess = { settings -> setState { copy(isLoading = false, values = settings.toFields()) } },
                onFailure = { e -> setState { copy(isLoading = false, loadError = errorCode(e)) } },
            )
        }
    }

    private fun save() {
        if (currentState.isSaving) return
        val errors = AiCreditSettingsField.entries.mapNotNull { field -> validate(field, currentState.values[field].orEmpty())?.let { field to it } }.toMap()
        if (errors.isNotEmpty()) {
            setState { copy(fieldErrors = errors, savedSettings = null) }
            return
        }
        val values = currentState.values
        fun amount(field: AiCreditSettingsField) = AiCreditFormat.parseDecimal(values.getValue(field)) ?: 0.0
        val settings = AiCreditSettings(
            enterpriseTrialEur = amount(AiCreditSettingsField.EnterpriseTrial),
            enterprisePaidEur = amount(AiCreditSettingsField.EnterprisePaid),
            driverTrialEur = amount(AiCreditSettingsField.DriverTrial),
            driverPaidEur = amount(AiCreditSettingsField.DriverPaid),
            usdPerEur = amount(AiCreditSettingsField.UsdPerEur),
            lowThresholdPercent = values.getValue(AiCreditSettingsField.LowThreshold).toInt(),
        )
        setState { copy(isSaving = true, saveError = null, savedSettings = null) }
        scope.launch {
            updateSettings(settings).fold(
                onSuccess = { saved -> setState { copy(isSaving = false, savedSettings = saved, values = saved.toFields()) } },
                onFailure = { e ->
                    // 400 error.validation.<field>: shown under the field concerned, the generic
                    // message only if none matches.
                    val badRequest = (e as? ApiException)?.takeIf { it.statusCode == HTTP_BAD_REQUEST }
                    val field = badRequest?.let { error -> FIELD_ERROR_CODES.entries.firstOrNull { error.message == it.value }?.key }
                    val code = if (badRequest != null) "validation" else errorCode(e)
                    setState {
                        copy(
                            isSaving = false,
                            saveError = code.takeIf { field == null },
                            fieldErrors = if (field != null) fieldErrors + (field to serverError(field)) else fieldErrors,
                        )
                    }
                },
            )
        }
    }

    private fun validate(field: AiCreditSettingsField, raw: String): AiCreditSettingsFieldError? {
        if (raw.isBlank()) return AiCreditSettingsFieldError.Required
        return when (field) {
            AiCreditSettingsField.LowThreshold -> {
                val value = raw.toIntOrNull() ?: 0
                if (value < AI_CREDIT_MIN_THRESHOLD || value > AI_CREDIT_MAX_THRESHOLD) AiCreditSettingsFieldError.ThresholdOutOfRange else null
            }
            AiCreditSettingsField.UsdPerEur -> {
                val value = AiCreditFormat.parseDecimal(raw) ?: 0.0
                when {
                    value <= 0 -> AiCreditSettingsFieldError.MustBePositive
                    value > AI_CREDIT_MAX_USD_PER_EUR -> AiCreditSettingsFieldError.RateTooHigh
                    else -> null
                }
            }
            else -> {
                val value = AiCreditFormat.parseDecimal(raw) ?: 0.0
                when {
                    value <= 0 -> AiCreditSettingsFieldError.MustBePositive
                    value > AI_CREDIT_MAX_LIMIT_EUR -> AiCreditSettingsFieldError.LimitTooHigh
                    else -> null
                }
            }
        }
    }

    /** The server rejects a value out of its range: the matching local message. */
    private fun serverError(field: AiCreditSettingsField) = when (field) {
        AiCreditSettingsField.LowThreshold -> AiCreditSettingsFieldError.ThresholdOutOfRange
        else -> AiCreditSettingsFieldError.MustBePositive
    }

    private fun AiCreditSettings.toFields() = mapOf(
        AiCreditSettingsField.EnterpriseTrial to AiCreditFormat.eurInput(enterpriseTrialEur, separator),
        AiCreditSettingsField.EnterprisePaid to AiCreditFormat.eurInput(enterprisePaidEur, separator),
        AiCreditSettingsField.DriverTrial to AiCreditFormat.eurInput(driverTrialEur, separator),
        AiCreditSettingsField.DriverPaid to AiCreditFormat.eurInput(driverPaidEur, separator),
        AiCreditSettingsField.UsdPerEur to AiCreditFormat.decimal(usdPerEur, RATE_DECIMALS, separator).trimEnd('0').removeSuffix(separator),
        AiCreditSettingsField.LowThreshold to lowThresholdPercent.toString(),
    )

    private fun errorCode(e: Throwable) = if (e is ApiException) "server_error" else "offline"

    private companion object {
        const val HTTP_BAD_REQUEST = 400
        const val EUR_DECIMALS = 2
        const val RATE_DECIMALS = 4
        const val RATE_WHOLE_DIGITS = 2
        // 3, not 2: "100" must stay "100" and get "Entre 1 et 99", never be cut to a silently valid "10".
        const val THRESHOLD_DIGITS = 3
        val FIELD_ERROR_CODES = mapOf(
            AiCreditSettingsField.EnterpriseTrial to "error.validation.enterprise_trial_eur",
            AiCreditSettingsField.EnterprisePaid to "error.validation.enterprise_paid_eur",
            AiCreditSettingsField.DriverTrial to "error.validation.driver_trial_eur",
            AiCreditSettingsField.DriverPaid to "error.validation.driver_paid_eur",
            AiCreditSettingsField.UsdPerEur to "error.validation.usd_per_eur",
            AiCreditSettingsField.LowThreshold to "error.validation.low_threshold_percent",
        )
    }
}
